import React from "react";
import SectionHeader from "../../../common/section-header/index.js";
import TextInput from "../../../common/text-input/index.js";
import DropdownComponent from "../../../common/dropdown-input/index.js";
import FileUpload from "../../../common/file-upload/index.js";
import { Checkbox } from "antd";
import Text from "../../../common/typography/text.js";
import CommonButton from "../../../common/button/button.js";
import AccountContactsSection from "./AccountContactsSection.js";
import {
  ACCOUNT_ENROLLMENT_SECTIONS,
  BILLING_SAME_AS_SHIPPING_KEY,
  BILLING_SHIPPING_MIRROR,
  applyBillingSameAsShipping,
  getEnrollmentFieldsForSection,
} from "../accountEnrollmentFields.js";

const AccountDetailsStep = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  optionsByField,
  lockedFields = {},
  baaFile = null,
  baaError = "",
  onBaaFileChange,
  derivedFields = {},
  helpByField = {},
}) => {
  const billingSameAsShipping = Boolean(values[BILLING_SAME_AS_SHIPPING_KEY]);

  const handleBillingSameAsShipping = (checked) => {
    setFieldValue(BILLING_SAME_AS_SHIPPING_KEY, checked);
    if (!checked) return;
    // Copy once on check; the wizard keeps the copy fresh while it stays on.
    const copied = applyBillingSameAsShipping(values);
    Object.keys(BILLING_SHIPPING_MIRROR).forEach((key) =>
      setFieldValue(key, copied[key]),
    );
  };

  const renderField = (field) => {
    const errorText = touched?.[field.key] ? errors?.[field.key] : "";
    // Billing fields are locked while they are being copied from shipping.
    const copiedFromShipping =
      billingSameAsShipping &&
      Object.prototype.hasOwnProperty.call(BILLING_SHIPPING_MIRROR, field.key);
    const derived = Boolean(derivedFields[field.key]) || copiedFromShipping;
    const valueKey = field.key;

    if (field.type === "select") {
      const options = optionsByField?.[valueKey] || [];
      const selected =
        options.find(
          (option) => String(option.value) === String(values[valueKey]),
        ) || null;

      // DropdownComponent has no helper-text slot; wrap it so derived / unmapped
      // notes render the same way TextInput renders its helperText.
      return (
        <div className="account-enrollment-select" key={field.key}>
        <DropdownComponent
          isDisabled={Boolean(lockedFields[field.key]) || derived}
          isRequired={Boolean(field.required)}
          isSearchable
          label={field.label}
          onChange={(option) => {
            setFieldValue(valueKey, option?.value ?? null);
            setFieldTouched(valueKey, true, false);
          }}
          options={options}
          parentClassname="account-enrollment-dropdown"
          placeholder={`Select ${field.label}`}
          value={selected}
        />
        {helpByField[field.key] ? (
          <Text color="lightText" variant="tinyText">
            {helpByField[field.key]}
          </Text>
        ) : null}
        </div>
      );
    }

    return (
      <TextInput
        key={field.key}
        errorText={errorText}
        helperText={helpByField[field.key]}
        isDisabled={derived}
        isRequired={Boolean(field.required)}
        onChange={(event) => {
          setFieldValue(valueKey, event.target.value);
          setFieldTouched(valueKey, true, false);
        }}
        placeholder={`Enter ${field.label}`}
        setValue={(value) => setFieldValue(valueKey, value)}
        titleText={field.label}
        value={values[valueKey] ?? ""}
      />
    );
  };

  return (
    <div className="account-enrollment-step">
      {ACCOUNT_ENROLLMENT_SECTIONS.map((section) => (
        <div className="account-enrollment-section" key={section.key}>
          <SectionHeader showButton={false} title={section.title} />
          {section.helperText ? (
            <Text
              className="account-enrollment-section-help"
              color="lightText"
              variant="tinyText"
            >
              {section.helperText}
            </Text>
          ) : null}
          {section.key === "billing" ? (
            <div className="account-enrollment-same-as-shipping">
              <Checkbox
                checked={billingSameAsShipping}
                onChange={(event) =>
                  handleBillingSameAsShipping(event.target.checked)
                }
              >
                Billing address is the same as shipping
              </Checkbox>
            </div>
          ) : null}
          <div className="account-enrollment-field-grid">
            {getEnrollmentFieldsForSection(section.key).map(renderField)}
          </div>
        </div>
      ))}

      <AccountContactsSection
        errors={errors}
        setFieldTouched={setFieldTouched}
        setFieldValue={setFieldValue}
        touched={touched}
        values={values}
      />

      {onBaaFileChange ? (
        <div className="account-enrollment-section">
          <SectionHeader showButton={false} title="Business Associate Agreement" />
          <div className="account-enrollment-field-grid">
            <div className="account-enrollment-baa">
              <FileUpload
                errorText={baaError}
                fileName={baaFile?.name || ""}
                helperText="Optional. PDF up to 4 MB. Attached to the account when the enrollment is approved."
                placeholder="Upload BAA (PDF)"
                titleText="BAA File"
                uploadProps={{
                  accept: ".pdf,application/pdf",
                  beforeUpload: (file) => {
                    onBaaFileChange(file);
                    return false;
                  },
                  fileList: baaFile ? [baaFile] : [],
                  maxCount: 1,
                  multiple: false,
                  showUploadList: false,
                }}
              />
              {baaFile ? (
                <CommonButton onClick={() => onBaaFileChange(null)} variant="link">
                  Remove file
                </CommonButton>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountDetailsStep;
